import React from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter, DrawerClose } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, User, Hash, DollarSign, Calendar, File } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface BillPreviewDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: any | null;
}

export const BillPreviewDrawer: React.FC<BillPreviewDrawerProps> = ({ open, onOpenChange, item }) => {
  const navigate = useNavigate();
  if (!item) return null;

  const amount = (() => {
    const v = item.extracted_data?.amount ?? item.amount;
    const n = parseFloat(String(v ?? 0));
    return isNaN(n) ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
  })();

  const customer = item.extracted_data?.customer_name ?? item.customer_name_raw ?? 'Unknown';
  const account = item.extracted_data?.account_number ?? item.account_number_raw ?? '—';
  const billDate = item.bill_date ? new Date(item.bill_date).toLocaleDateString() : '—';

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" /> Bill Preview
          </DrawerTitle>
          <DrawerDescription>Quick peek of the uploaded file and extracted fields.</DrawerDescription>
        </DrawerHeader>

        <div className="px-6 pb-2 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <div>
                <div className="text-sm text-muted-foreground">Customer</div>
                <div className="font-medium">{customer}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Hash className="h-4 w-4" />
              <div>
                <div className="text-sm text-muted-foreground">Account</div>
                <div className="font-medium">{account}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              <div>
                <div className="text-sm text-muted-foreground">Amount</div>
                <div className="font-medium">{amount}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <div>
                <div className="text-sm text-muted-foreground">Bill Date</div>
                <div className="font-medium">{billDate}</div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Badge variant="secondary">{item.mime_type?.toUpperCase() || 'FILE'}</Badge>
            <span className="text-sm text-muted-foreground truncate">{item.file_name}</span>
          </div>
        </div>

        <DrawerFooter>
          <Button onClick={() => navigate(`/dashboard/production-review/${item.id}`)}>
            Open Full View
          </Button>
          <DrawerClose asChild>
            <Button variant="outline">Close</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
